import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Check, CheckCircle, Package, Money, Truck, X, ChatCircle } from '@phosphor-icons/react'
import api from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useWebSocketOptional } from '../context/WebSocketContext'

interface Notification {
  id: string
  type: string
  title: string
  message: string
  reference_id: string
  read: boolean
  created_at: string
}

const NOTIFICATION_ICONS: Record<string, typeof Bell> = {
  new_quote: Package,
  new_offer: Money,
  offer_accepted: CheckCircle,
  offer_declined: X,
  order_update: Truck,
  new_chat_message: ChatCircle,
}

const NOTIFICATION_COLORS: Record<string, string> = {
  new_quote: 'bg-blue-100 text-blue-600',
  new_offer: 'bg-emerald-100 text-emerald-600',
  offer_accepted: 'bg-green-100 text-green-600',
  offer_declined: 'bg-red-100 text-red-600',
  order_update: 'bg-orange-100 text-orange-600',
  new_chat_message: 'bg-purple-100 text-purple-600',
}

export function NotificationBell() {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { user } = useAuth()
  const ws = useWebSocketOptional()

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const response = await api.get<{
        notifications: Notification[]
        unreadCount: number
      }>('/notifications?limit=10')

      setNotifications(response.data.notifications)
      setUnreadCount(response.data.unreadCount)
    } catch (error) {
      console.error('Error fetching notifications:', error)
    }
  }

  // Fetch unread count only (lightweight polling)
  const fetchUnreadCount = async () => {
    try {
      const response = await api.get<{ unreadCount: number }>('/notifications/unread-count')
      setUnreadCount(response.data.unreadCount)
    } catch (error) {
      console.error('Error fetching unread count:', error)
    }
  }

  // Initial fetch and polling
  useEffect(() => {
    if (!user) return

    fetchNotifications()

    // Poll every 30 seconds for unread count
    const interval = setInterval(fetchUnreadCount, 30000)

    return () => clearInterval(interval)
  }, [user])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Refresh notifications when opening dropdown
  useEffect(() => {
    if (isOpen) {
      fetchNotifications()
    }
  }, [isOpen])

  // Realtime: update the bell the instant a notification arrives (incl. new
  // chat messages, Plane #7) instead of waiting for the 30s poll.
  useEffect(() => {
    if (!user || !ws) return
    const unsubscribe = ws.onNotification(() => {
      fetchNotifications()
    })
    return unsubscribe
  }, [user, ws])

  const markAsRead = async (id: string) => {
    try {
      await api.post(`/notifications/${id}/read`)
      setNotifications(prev =>
        prev.map(n => (n.id === id ? { ...n, read: true } : n))
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  const markAllAsRead = async () => {
    try {
      setLoading(true)
      await api.post('/notifications/read-all')
      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
      setUnreadCount(0)
    } catch (error) {
      console.error('Error marking all as read:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    if (!notification.read) {
      markAsRead(notification.id)
    }

    // Navigate based on notification type
    const companyType = user?.current_company?.type

    switch (notification.type) {
      case 'new_quote':
        navigate(`/freight-forwarder/opportunities/${notification.reference_id}`)
        break
      case 'new_offer':
        // reference_id is offer_id, but we need to go to the quote
        // For now, go to quotes list
        navigate('/shipper/quotes')
        break
      case 'offer_accepted':
        if (companyType === 'freight-forwarder') {
          navigate('/freight-forwarder/offers')
        }
        break
      case 'offer_declined':
        if (companyType === 'freight-forwarder') {
          navigate('/freight-forwarder/opportunities')
        }
        break
      case 'order_update':
        if (companyType === 'shipper') {
          navigate(`/shipper/orders/${notification.reference_id}`)
        } else {
          navigate(`/freight-forwarder/orders/${notification.reference_id}`)
        }
        break
      case 'new_chat_message':
        // Parse reference_id which can be "orderId" or "orderId:messageId"
        const parts = notification.reference_id.split(':')
        const orderId = parts[0]
        const messageId = parts[1]
        const basePath = companyType === 'shipper' ? '/shipper' : '/freight-forwarder'
        if (messageId) {
          navigate(`${basePath}/orders/${orderId}?tab=chat&messageId=${messageId}`)
        } else {
          navigate(`${basePath}/orders/${orderId}?tab=chat`)
        }
        break
    }

    setIsOpen(false)
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'Agora'
    if (minutes < 60) return `${minutes}min`
    if (hours < 24) return `${hours}h`
    if (days < 7) return `${days}d`
    return date.toLocaleDateString('pt-BR')
  }

  if (!user) return null

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
      >
        <Bell size={24} weight={unreadCount > 0 ? 'fill' : 'regular'} />

        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 flex items-center justify-center px-1 text-xs font-bold text-white bg-red-500 rounded-full animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50">
          {/* Header */}
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Notificacoes</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                disabled={loading}
                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1 disabled:opacity-50"
              >
                <Check size={16} weight="bold" />
                Marcar todas como lidas
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-12 px-4 text-center">
                <Bell size={48} className="mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 text-sm">Nenhuma notificacao</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => {
                  const Icon = NOTIFICATION_ICONS[notification.type] || Bell
                  const colorClass = NOTIFICATION_COLORS[notification.type] || 'bg-gray-100 text-gray-600'

                  return (
                    <button
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex gap-3 ${
                        !notification.read ? 'bg-blue-50/50' : ''
                      }`}
                    >
                      {/* Icon */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                        <Icon size={20} weight="fill" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}>
                            {notification.title}
                          </p>
                          <span className="text-xs text-gray-400 flex-shrink-0">
                            {formatTime(notification.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 truncate mt-0.5">
                          {notification.message}
                        </p>
                      </div>

                      {/* Unread Indicator */}
                      {!notification.read && (
                        <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-2" />
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
              <button
                onClick={() => {
                  setIsOpen(false)
                  // Navigate to full notifications page based on current company type
                  const basePath = user?.current_company?.type === 'freight-forwarder'
                    ? '/freight-forwarder'
                    : '/shipper'
                  navigate(`${basePath}/notifications`)
                }}
                className="w-full text-center text-sm text-gray-600 hover:text-gray-900 font-medium"
              >
                Ver todas as notificacoes
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
